import i18n from "@/i18n";
import { SystemLanguages } from "@/shared/system";
import type { i18n as I18nInstance } from "i18next";
import { type ReactNode, useEffect, useRef, useState } from "react";
import { I18nextProvider } from "react-i18next";

const SUPPORTED_I18N_LANGS = SystemLanguages.map((l) => l.value);

const COUNTRY_LANG_TO_I18N: Record<number, string> = {
  1: "nl",
  2: "de",
  3: "en",
  4: "fr",
  5: "es",
  6: "tr",
  7: "sr",
  8: "nl",
  9: "fr",
  10: "zh",
  11: "pl",
  12: "ar",
  13: "ja",
  14: "ko",
  15: "sr",
  16: "ru",
  17: "ur",
};

/** Languages usable as UI locale for pickup settings. */
export const screenUiLanguages = SystemLanguages;

export const getScreenLanguageKey = (language?: string | number) => {
  if (typeof language === "string" && language) {
    return SUPPORTED_I18N_LANGS.includes(language) ? language : "nl";
  }

  const id = Number(language);
  if (!Number.isFinite(id)) return "nl";

  return COUNTRY_LANG_TO_I18N[id] ?? "nl";
};

const waitForI18n = () =>
  i18n.isInitialized
    ? Promise.resolve()
    : new Promise<void>((resolve) => {
        const onInitialized = () => {
          i18n.off("initialized", onInitialized);
          resolve();
        };
        i18n.on("initialized", onInitialized);
      });

/**
 * Scopes translations to screen page content only.
 * Uses a cloned i18n instance so the global app language stays unchanged.
 */
const ScreenLanguageProvider = ({
  language = "nl",
  children,
}: {
  language?: string | number;
  children: ReactNode;
}) => {
  const lng = getScreenLanguageKey(language);
  const instanceRef = useRef<I18nInstance | null>(null);
  const [instance, setInstance] = useState<I18nInstance | null>(null);
  const [readyLng, setReadyLng] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      try {
        await waitForI18n();
        if (cancelled) return;

        if (typeof i18n.cloneInstance !== "function") {
          setFailed(true);
          return;
        }

        if (!instanceRef.current) {
          const cloned = i18n.cloneInstance({
            lng,
            fallbackLng: "nl",
            react: {
              useSuspense: false,
            },
          });
          // Extra guard for react-i18next suspense
          cloned.options.react = {
            ...(cloned.options.react || {}),
            useSuspense: false,
          };

          await cloned.loadLanguages([lng, "nl"]);
          await cloned.changeLanguage(lng);
          if (cancelled) return;

          instanceRef.current = cloned;
          setInstance(cloned);
          setReadyLng(lng);
          return;
        }

        setReadyLng(null);
        await instanceRef.current.loadLanguages(lng);
        await instanceRef.current.changeLanguage(lng);
        if (!cancelled) {
          setReadyLng(lng);
        }
      } catch (error) {
        console.error("Failed to init screen language", error);
        if (!cancelled) setFailed(true);
      }
    };

    void run();

    return () => {
      cancelled = true;
    };
  }, [lng]);

  if (failed) {
    return <>{children}</>;
  }

  if (!instance || readyLng !== lng) {
    return null;
  }

  return <I18nextProvider i18n={instance}>{children}</I18nextProvider>;
};

export default ScreenLanguageProvider;
